import { type ActorRefFromLogic, type SnapshotFrom } from "xstate";

import {
	GIT_PUBLISHING_BRANCH_NAME,
	GIT_PUBLISHING_REMOTE_NAME,
} from "~/state/machines/git/addGitMomentRemote";
import type { BranchInfo as GitBranchInfo } from "~/state/machines/git/gitBranches.machine";
import type {
	GitRemote,
	GitRepoStatus,
} from "~/state/machines/git/repository/git-repository.types";
import { getDocsApiUrl } from "~/utils/docsapi";

import { type AuthenticationMachine } from "../authentication";
import {
	type PlanStatus,
	type PlannedOutcome,
	type PublishStatus,
	type SyncMode,
	type SyncPlan,
	type SyncPlanMaybe,
	emptySyncPlan,
	publishSyncPlan,
	shareSyncPlan,
} from "./plans";

export function syncPlan(args: {
	syncMode: SyncMode;

	authenticationSnap: SnapshotFrom<ActorRefFromLogic<AuthenticationMachine>>;

	gitRepoStatus: SyncPlanMaybe<GitRepoStatus | undefined>;
	trackingBranches: SyncPlanMaybe<GitBranchInfo[]>;
	remotes: SyncPlanMaybe<GitRemote[]>;

	remoteRepoExists: SyncPlanMaybe<boolean>;
	hasSharePermission: SyncPlanMaybe<boolean>;
	isPublished: SyncPlanMaybe<boolean>;
}): SyncPlan {
	const {
		syncMode,
		authenticationSnap,

		gitRepoStatus,
		trackingBranches,
		remotes,

		remoteRepoExists,
		hasSharePermission: hasSharePermission,
		isPublished,
	} = args;

	// if any git statuses fail, immediately return the empty plan
	if (gitRepoStatus === "unknown" || trackingBranches === "unknown" || remotes === "unknown") {
		return emptySyncPlan();
	}

	const { authnStatus } = authenticationSnap.context;
	const isAuthenticated = authnStatus === "authenticated";

	// note that if the repository is uninitialized, then we are defacto on
	// the publishing branch (because it will be initialized into main). however
	// this shouldn't happen because the repo should be init'd during document load.
	const isOnPublishBranch =
		(gitRepoStatus && gitRepoStatus.branch === GIT_PUBLISHING_BRANCH_NAME) || !gitRepoStatus;
	const documentRemoteSet =
		remotes?.find(
			(remote) =>
				remote.name === GIT_PUBLISHING_REMOTE_NAME && remote.url.startsWith(getDocsApiUrl())
		) !== undefined;
	const trackingBranchSet = !!trackingBranches?.find(
		(branch) => branch.name === GIT_PUBLISHING_BRANCH_NAME
	)?.upstream;

	const branch = gitRepoStatus && gitRepoStatus?.branch ? gitRepoStatus.branch : undefined;
	const ahead = gitRepoStatus && gitRepoStatus?.ahead ? gitRepoStatus?.ahead : 0;
	const behind = gitRepoStatus && gitRepoStatus?.behind ? gitRepoStatus?.behind : 0;
	const repoClean = gitRepoStatus && gitRepoStatus?.isClean;
	const repoAhead = ahead > 0;

	let actions;
	let plannedOutcome: PlannedOutcome = null;

	switch (syncMode) {
		case "publish-if-unpublished":
		case "try-sync":
			[actions, plannedOutcome] = publishSyncPlan({
				syncMode,
				isOnPublishBranch,
				remoteRepoExists,
				gitRepoStatus,
				documentRemoteSet,
				trackingBranchSet,
				repoClean,
				repoAhead,
				isPublished,
			});
			break;
		case "share-sync":
			[actions, plannedOutcome] = shareSyncPlan({
				isOnPublishBranch,
				remoteRepoExists,
				gitRepoStatus,
				documentRemoteSet,
				trackingBranchSet,
				repoClean,
				repoAhead,
			});
			break;
		default:
			throw new Error(`Unknown sync mode: ${syncMode}`);
	}

	let planStatus: PlanStatus;
	if (!isAuthenticated) {
		planStatus = "unachievable-not-authenticated";
	} else if (gitRepoStatus && !isOnPublishBranch) {
		planStatus = "unachievable-not-on-synced-branch";
	} else if (remoteRepoExists === true && !(hasSharePermission === true)) {
		// TODO: check for workspace push permissions if the repository does not exist upstream,
		// which is defined as workspace ownership permissions today.
		planStatus = "unachievable-unauthorized";
	} else if (gitRepoStatus && gitRepoStatus.behind > 0) {
		planStatus = "unachievable-behind-remote";
	} else if (remoteRepoExists === true && isPublished === "unknown") {
		planStatus = "status-pending";
	} else if (actions.length === 0) {
		planStatus = "unachievable-nothing-to-do";
	} else {
		planStatus = "achievable";
	}

	let publishStatus: PublishStatus;
	if (remoteRepoExists === false) {
		publishStatus = "unpublished";
	} else if (remoteRepoExists === true && isPublished === false) {
		publishStatus = "unpublished";
	} else if (remoteRepoExists === true && isPublished === true) {
		publishStatus = "published";
	} else {
		publishStatus = "status-pending";
	}

	return {
		planStatus,
		publishStatus,
		actions,
		plannedOutcome,
		branch,
		ahead,
		behind,
	};
}
